import React from "react";
import { useContext, Fragment } from "react";
import { Tag } from "../elements/tag/tag";
import { useMedia } from "react-use";
import styled from "@emotion/styled";
import Loader from "react-loader-spinner";
import { ReadMore } from "../elements/readMore";
import { formatDate } from "../../utils/date-functions";
import { Accordion } from "../elements/accordion/accordion";
import { AccordionPanel } from "../elements/accordion/accordion-panel";
import { Description } from "../elements/text/description";
import { ThemeContext, Theme } from "../../theme/provider/theme-provider";
import { Link } from "../elements/text/Link";
import { LinkIcon } from "../elements/icons/link";

interface IEventResultsProps {
  events: any[];
  loading: boolean;
}

const StyledCardHeader = styled.h3`
  margin: 0.5rem 0;

  @media screen and (max-width: 480px) {
    font-size: 110%;
  }
`;

const StyledSub = styled.span<Theme>`
  font-weight: 600;
  color: ${(props) => props.colors.white};
  background-color: ${(props) => props.colors.primary};
  padding: 0.2rem 0.5rem;
  display: inline-block;
  border-top-right-radius: 3px;
  border-top-left-radius: 3px;
  margin-left: 1rem;
`;

const StyledCardVody = styled.div`
  padding-top: 1rem;
`;

const StyledContainer = styled.div`
  @media screen and (max-width: 480px) {
    font-size: 90%;
  }
`;

const AlignedLinkIcon = styled(LinkIcon)`
  vertical-align: bottom;
`;

const StyledLoader = styled(Loader)`
  display: flex;
  justify-content: center;
`;

const StyledSection = styled.div`
  margin-top: 2rem;
  position: relative;
`;

const getAmount = (amounts: number | number[], currency: string) => {
  if (!Array.isArray(amounts)) {
    amounts = [amounts];
  }

  return amounts
    .map(
      (amount) =>
        `${new Intl.NumberFormat(undefined, {
          minimumFractionDigits: 2,
        }).format(amount)} ${currency}`
    )
    .join(", ");
};

const getVenue = (event: any, color: string) => {
  if (event.city?.id !== "online") {
    return (
      <Fragment>
        <AlignedLinkIcon color={color} />
        <Link
          title={`Go to google maps`}
          href={`https://www.google.com/maps/search/?api=1&query=${event.venue}`}
        >
          {event.venue}
        </Link>
      </Fragment>
    );
  }

  return event.venue;
};

export const EventResults = ({ events, loading }: IEventResultsProps) => {
  const isLarge = useMedia("min-width:768px");
  const { theme } = useContext(ThemeContext);

  const { colors } = theme;

  if (loading) {
    return (
      <StyledLoader
        type="ThreeDots"
        color={colors.primary}
        height={40}
        width={40}
      />
    );
  }
  return (
    <div className="row">
      <StyledContainer className="col-xs-12 col-md-8 col-md-offset-2 col-lg-10 col-lg-offset-1">
        <Accordion>
          {events?.map((event: any, index) => (
            <AccordionPanel
              key={index}
              id={index}
              section={
                index === 0 || event.month !== events[index - 1].month ? (
                  <StyledSection>
                    <StyledSub {...theme}>{event.month}</StyledSub>
                  </StyledSection>
                ) : null
              }
              header={
                <div>
                  <div>
                    <StyledCardHeader>{event.title}</StyledCardHeader>
                  </div>
                  <div>
                    <Tag color={colors.secondary}>{event.eventType?.value}</Tag>
                    <Tag
                      color={
                        event.entryfee === "Paid"
                          ? colors.secondary
                          : colors.white
                      }
                      borderColor={
                        event.entryfee === "Paid" ? undefined : colors.secondary
                      }
                      fontColor={
                        event.entryfee !== "Paid" ? colors.secondary : undefined
                      }
                    >
                      {event.entryfee}
                    </Tag>
                  </div>
                </div>
              }
            >
              <StyledCardVody>
                <Description>
                  <ReadMore
                    text={event.description}
                    maxLength={isLarge ? 200 : 100}
                  />
                </Description>

                <Description
                  label={event.artists?.length > 1 ? "Artists" : "Artist"}
                >
                  {event.artists?.join(", ")}
                </Description>

                <Description
                  label={event.startDate && event.endDate ? "Dates" : "Date"}
                >
                  {event.startDate && event.endDate
                    ? `${formatDate(
                        event.startDate,
                        "EEE, dd MMMM yyyy"
                      )} to ${formatDate(event.endDate, "EEE, dd MMMM yyyy")}`
                    : event.startDate
                    ? formatDate(event.startDate, "EEE, dd MMMM yyyy")
                    : undefined}
                </Description>

                <Description label="Time">
                  {event.starttime && event.endtime
                    ? `${event.starttime} - ${event.endtime}`
                    : event.starttime}
                </Description>

                <Description label="Entry">
                  <AlignedLinkIcon color={colors.primary} />
                  <Link
                    title={`Go to ${event.ticketingregistrationlink}`}
                    href={event.ticketingregistrationlink}
                    subject={event.title}
                  >
                    {event.entryfee === "Paid"
                      ? getAmount(event.ticketprice, event.currency)
                      : event.entryfee}
                  </Link>
                </Description>

                <Description label="Venue">
                  {getVenue(event, colors.primary)}
                </Description>
              </StyledCardVody>
            </AccordionPanel>
          ))}
        </Accordion>
      </StyledContainer>
    </div>
  );
};
